import type { Product } from "@/api";
import { productHref } from "@/lib/product-link";

/**
 * Плитка выдачи — один вариант-цвет товара.
 *
 * Айталы 02.10 (A-09): «Цену выводить точную, именно варианта товара, который
 * на фото. Все варианты товара — это отдельные карточки товара». Вариант на
 * шлюзе — строка цвет × размер, а вариант для покупателя — цвет со своими фото
 * (страница товара группирует так же). Поэтому товар с двумя цветами даёт в
 * каталоге, ленте и подборках две плитки: у каждой своё фото, своя цена (без
 * «от») и ссылка `/product/<id>?variant=<id строки>` — страница товара читает
 * `variant` и открывается на этом цвете.
 */
export type ProductTile = {
	/** Ключ для React: товар + цвет. */
	key: string;
	product: Product;
	/** Строка варианта, чью цену показывает плитка; её id уходит в `?variant=`. */
	variantId?: string;
	/** Цвет плитки; пусто, если у товара нет цветов. */
	color?: string;
	photo?: string;
	price: number;
	oldPrice?: number;
	/** Остаток плитки: сумма по размерам её цвета. */
	stock: number;
	href: string;
};

/** Условия выдачи, под которые подбираются плитки товара. */
export type TileFilter = {
	colors?: string[];
	priceMin?: number;
	priceMax?: number;
	sale?: boolean;
	inStockOnly?: boolean;
};

type Variant = NonNullable<Product["variants"]>[number];

const colorKey = (color: string | undefined | null): string => color?.trim().toLowerCase() ?? "";

// Та же витрина, что у шлюза (aggregateVariants): самый дешёвый размер в
// наличии, а если в наличии нет ничего — самый дешёвый вообще.
const displayVariant = (variants: Variant[]): Variant | undefined => {
	const byPrice = (a: Variant, b: Variant) => a.price - b.price || (a.position ?? 0) - (b.position ?? 0);
	return [...variants].filter((v) => v.stock > 0).sort(byPrice)[0] ?? [...variants].sort(byPrice)[0];
};

const hrefOf = productHref;

/** Плитки одного товара: по одной на цвет (товар без цветов — одна плитка). */
export function productTiles(product: Product, filter: TileFilter = {}): ProductTile[] {
	const variants = product.variants ?? [];
	// Цвета в порядке, в каком их расставил продавец.
	const groups = new Map<string, Variant[]>();
	for (const v of [...variants].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
		const key = colorKey(v.color);
		if (!key) continue;
		const list = groups.get(key) ?? [];
		list.push(v);
		groups.set(key, list);
	}

	if (groups.size <= 1) {
		// Один цвет или без цветов: плитка — сам товар, цена — та, что на фото
		// (витринный размер), без «от».
		const shown = displayVariant(variants);
		return [
			{
				key: product.id,
				product,
				variantId: shown?.id,
				color: shown?.color || product.color || undefined,
				photo: product.photos[0] ?? shown?.photos[0],
				price: shown?.price ?? product.price,
				oldPrice: shown ? shown.oldPrice : product.oldPrice,
				stock: product.stock,
				href: hrefOf(product.id, shown?.id),
			},
		];
	}

	const madeToOrder = product.madeToOrder === true;
	const all: ProductTile[] = [];
	let first = true;
	for (const [key, group] of groups) {
		const shown = displayVariant(group) as Variant;
		const ownPhoto = group.find((v) => v.photos.length > 0)?.photos[0];
		all.push({
			key: `${product.id}:${key}`,
			product,
			variantId: shown.id,
			color: shown.color,
			// Своё фото цвета; у первого цвета без своих фото — обложка товара.
			photo: ownPhoto ?? (first ? product.photos[0] : undefined) ?? product.photos[0],
			price: shown.price,
			oldPrice: shown.oldPrice,
			stock: group.reduce((sum, v) => sum + v.stock, 0),
			href: hrefOf(product.id, shown.id),
		});
		first = false;
	}

	// Закончившийся цвет в выдаче не рекламируем (как шлюз — закончившийся
	// товар), если только товар не шьют на заказ.
	let tiles = madeToOrder ? all : all.filter((t) => t.stock > 0);
	if (tiles.length === 0) tiles = all.slice(0, 1);

	// Условия выдачи сужают и плитки: по фильтру «Синий» — только синий вариант,
	// по цене и SALE — только подходящие цвета. Если под условие не подошёл ни
	// один цвет (шлюз нашёл товар по основному цвету карточки), оставляем все.
	const wanted = new Set((filter.colors ?? []).map(colorKey).filter(Boolean));
	const fits = (t: ProductTile) =>
		(wanted.size === 0 || wanted.has(colorKey(t.color))) &&
		(filter.priceMin == null || t.price >= filter.priceMin) &&
		(filter.priceMax == null || t.price <= filter.priceMax) &&
		(!filter.sale || (t.oldPrice != null && t.oldPrice > t.price)) &&
		(!filter.inStockOnly || t.stock > 0);
	const narrowed = tiles.filter(fits);
	return narrowed.length > 0 ? narrowed : tiles;
}

/** Плитки выдачи: товары, развёрнутые по цветам, в исходном порядке. */
export function expandProductTiles(products: Product[], filter: TileFilter = {}): ProductTile[] {
	return products.flatMap((p) => productTiles(p, filter));
}
